import { Pencil } from 'lucide-react'
import type { RouterOutputs } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { SheetDescription, SheetHeader, SheetTitle } from 'shared/components/ui/sheet'
import { useTranslate } from 'shared/lib/i18n'
import { LocationJournal } from './LocationJournal'
import { PointClosure } from './PointClosure'
import { PointInventory } from './PointInventory'
import { PointSwitch } from './PointSwitch'
import { PointWeek } from './PointWeek'

type Location = RouterOutputs['locations']['List'][number]

/**
 * One place in the side panel of «Локации»: its id, name, type and region; for a
 * Point — whether it is on TezGo (and its drain and rollback), its closure, its week
 * and, on TezGo, its inventory; for every place — its journal, read when asked for.
 */
export function LocationSheet({
	location,
	points,
	onChanged,
	onEdit,
}: {
	location: Location
	/** Every place, for the choice of a recipient when a Point closes for good. */
	points: readonly Location[]
	onChanged: () => void
	onEdit: () => void
}) {
	const { t } = useTranslate()
	const isPoint = location.type === 'point'
	return (
		<div className='flex flex-col gap-8 pb-8' data-testid={`location-${location.id}`}>
			<SheetHeader className='pr-14 pb-0'>
				<div className='flex items-start gap-3'>
					<div className='flex min-w-0 flex-col gap-1'>
						<SheetTitle className='flex items-center gap-2 text-lg'>
							<span className='font-mono font-semibold' data-testid='location-id'>
								{location.id}
							</span>
							<span className='truncate font-normal'>{location.name}</span>
						</SheetTitle>
						<SheetDescription>
							{t(`locationType.${location.type}`)} · {location.region}
						</SheetDescription>
					</div>
					<Button variant='outline' size='sm' className='ml-auto' onClick={onEdit} data-testid='location-sheet-edit'>
						<Pencil />
						{t('locations.edit')}
					</Button>
				</div>
			</SheetHeader>

			<div className='flex flex-col gap-8 px-4'>
				{isPoint && (
					<>
						<PointSwitch location={location} onChanged={onChanged} />
						{(location.migrated || location.closedForGood) && (
							<PointClosure location={location} points={points} onChanged={onChanged} />
						)}
						<PointWeek
							key={location._id}
							locationId={location._id}
							schedule={location.schedule}
							onChanged={onChanged}
						/>
						{location.migrated && <PointInventory location={location} />}
					</>
				)}
				<LocationJournal key={location._id} locationId={location._id} />
			</div>
		</div>
	)
}
